import React, { useMemo } from "react";
import { Stack, Text, Group } from "@mantine/core";

import { RunningMessage } from "@/utils/api/RunningMessageService";
import {
  AppTable,
  SortConfig,
  AppTableColumn,
  StatusBadge,
  RecordActions,
  AppButton,
} from "@/components/generic";
import {
  formatDateTime,
  formatDaysOfWeek,
  formatUTCDateRange,
  formatUTCTimeRange,
} from "@/utils";
import { MESSAGE_SOURCE_OPTIONS } from "../MessageSourceSelector";

export interface RunningMessagesTableProps {
  /** Array of running messages to display */
  runningMessages: RunningMessage[];

  /** Loading state for initial load */
  loading: boolean;

  /** Loading state for loading more items */
  loadingMore: boolean;

  /** Whether there are more items to load */
  hasMore: boolean;

  /** Current sort configuration */
  sortBy: SortConfig[];

  /** Callback when sort changes */
  onSortChange: (sortBy: SortConfig[]) => void;

  /** Callback to load more items */
  onLoadMore: () => void;

  /** Callback when edit action is clicked */
  onEditMessage?: (message: RunningMessage) => void;

  /** Callback when duplicate action is clicked */
  onDuplicateMessage?: (message: RunningMessage) => void;

  /** Callback when remove action is clicked */
  onRemoveMessage?: (message: RunningMessage) => void;

  /** Callback when assign to displays action is clicked */
  onAssignMessage?: (eunningMessage: RunningMessage) => void;

  /** ID of message currently being removed */
  removingMessageId?: string | null;

  /** Table size */
  size?: "xs" | "sm" | "md" | "lg" | "xl";
}

export const RunningMessagesTable: React.FC<RunningMessagesTableProps> = ({
  runningMessages,
  loading,
  loadingMore,
  hasMore,
  sortBy,
  onSortChange,
  onLoadMore,
  onEditMessage,
  onDuplicateMessage,
  onRemoveMessage,
  onAssignMessage,
  removingMessageId,
  size = "md",
}) => {
  // Helper function to get message source info
  const getMessageSourceInfo = (sourceType?: string) => {
    const option = MESSAGE_SOURCE_OPTIONS.find(
      (opt) => opt.value === sourceType
    );
    return option || { label: "Unknown", icon: "HelpCircle", color: "gray" };
  };

  // Helper function to format message display
  const formatMessageDisplay = (
    message: RunningMessage
  ): { title: string; subtitle: string } => {
    const title = message.name || "Unnamed Message";
    const subtitle = message.messageText || "No message text";
    return { title, subtitle };
  };

  // Helper function to truncate text
  const truncateText = (text?: string, maxLength: number = 60): string => {
    if (!text) return "";
    return text.length > maxLength
      ? `${text.substring(0, maxLength)}...`
      : text;
  };

  // Define table columns
  const columns: AppTableColumn<RunningMessage>[] = useMemo(
    () => [
      {
        key: "name",
        label: "Message",
        sortable: true,
        flex: true,
        minWidth: 200,
        render: (_, record) => {
          const { title, subtitle } = formatMessageDisplay(record);

          return (
            <Stack gap={0}>
              <Text fw={500} lineClamp={1}>
                {title}
              </Text>
              <Text size="sm" c="dimmed" lineClamp={2}>
                {truncateText(subtitle)}
              </Text>
            </Stack>
          );
        },
      },
      {
        key: "messageSourceType",
        label: "Message Type",
        sortable: true,
        width: 180,
        minWidth: 180,
        render: (_, record) => {
          const sourceInfo = getMessageSourceInfo(record.messageSourceType);
          return <Text size="sm">{sourceInfo.label}</Text>;
        },
      },
      {
        key: "schedule",
        label: "Schedule",
        sortable: false,
        width: 200,
        minWidth: 200,
        hideOnMobile: true,
        render: (_, record) => {
          const hasDateRange = record.startDatetime || record.endDatetime;
          const hasTimeRange = record.startTime || record.endTime;
          const hasRecurrence =
            record.recurrencePattern &&
            record.recurrencePattern.toUpperCase() !== "NONE";

          // If no schedule info at all
          if (!hasDateRange && !hasTimeRange && !hasRecurrence) {
            return (
              <Text size="sm" c="dimmed">
                Always active
              </Text>
            );
          }

          return (
            <Stack gap={2}>
              {/* Date Range */}
              {hasDateRange && (
                <Group gap={4} wrap="nowrap">
                  <Text size="xs" c="dimmed" style={{ minWidth: "35px" }}>
                    Dates:
                  </Text>
                  <Text size="sm" lineClamp={1}>
                    {formatUTCDateRange(
                      record.startDatetime,
                      record.endDatetime
                    )}
                  </Text>
                </Group>
              )}

              {/* Time Range */}
              {hasTimeRange && (
                <Group gap={4} wrap="nowrap">
                  <Text size="xs" c="dimmed" style={{ minWidth: "35px" }}>
                    Time:
                  </Text>
                  <Text size="sm" lineClamp={1}>
                    {formatUTCTimeRange(record.startTime, record.endTime)}
                  </Text>
                </Group>
              )}

              {/* Recurrence Pattern */}
              {hasRecurrence && (
                <Group gap={4} wrap="nowrap">
                  <Text size="xs" c="dimmed" style={{ minWidth: "35px" }}>
                    Days:
                  </Text>
                  <Text size="sm" lineClamp={1}>
                    {formatDaysOfWeek(
                      record.recurrenceDays,
                      record.recurrencePattern
                    )}
                  </Text>
                </Group>
              )}
            </Stack>
          );
        },
      },
      {
        key: "status",
        label: "Status",
        sortable: true,
        width: 120,
        minWidth: 120,
        align: "left",
        hideOnMobile: true,
        render: (_, record) => {
          return <StatusBadge status={record.status} />;
        },
      },
      {
        key: "updatedAt",
        label: "Updated",
        sortable: true,
        width: 120,
        minWidth: 120,
        render: (_, record) => (
          <Text size="sm" c="dimmed">
            {formatDateTime(record.updatedAt || record.createdAt, "datetime")}
          </Text>
        ),
      },
      {
        key: "actions",
        label: "",
        width: 270,
        minWidth: 270,
        align: "left",
        render: (_, record) => (
          <Group gap={"xs"} justify="flex-end">
            <AppButton
              key="playlist"
              variant="tertiary"
              iconName="DeviceTv"
              size={"xs"}
              onClick={() =>
                typeof onAssignMessage == "function" && onAssignMessage(record)
              }
              tooltip="Assign the running message to displays"
            />
            <RecordActions
              context={record}
              onEditClick={onEditMessage}
              onDuplicateClick={onDuplicateMessage}
              onRemoveClick={onRemoveMessage}
              //onGoInClick={onAssignMessage}
              removeInProgress={removingMessageId === record.id}
            />
          </Group>
        ),
      },
    ],
    [
      onEditMessage,
      onDuplicateMessage,
      onRemoveMessage,
      onAssignMessage,
      removingMessageId,
    ]
  );

  return (
    <AppTable
      data={runningMessages}
      hideOnNoData={true}
      columns={columns}
      loading={loading}
      hasMore={hasMore}
      loadingMore={loadingMore}
      onLoadMore={onLoadMore}
      sortBy={sortBy}
      onSortChange={onSortChange}
      multiSort={false}
      getRowKey={(message) => message?.id || ""}
      withBorder
      highlightOnHover
      size={size}
    />
  );
};
